import React, { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { getTasksItemPhotos } from '@app/api/routes/monitoringTasks'

import { ContentLayout } from '@app/layouts'
import { ExtendedTable, TableSettingsToolbar } from '@app/components'

import { MONITORINGS_TASKS_KEY } from '@app/constants/queryKeys'
import { TABLE_SETTING_KEYS } from '@app/constants/tableSettingKeys'

import { usePaginationQueryParams, useErrorRedirect, useTableSearch, useTableSetting } from '@app/hooks'

import { getColumns, adaptCompetitorsPhotoControlDataSource } from './lib'
import { columnsSearchSchema, tableSearchQuerySchema } from './configs'

import type { ReactNode } from 'react'
import type { CompetitorsPhotoProps } from './types'
import type { GetTasksItemPhotosParams } from '@app/api/routes/monitoringTasks/types'

const columns = getColumns()

export const PhotoControlTab: React.FC<CompetitorsPhotoProps> = ({ taskId }) => {
  const { paginationParams, pagination, setPagination } = usePaginationQueryParams()

  const tasksItemPhotosParams = useMemo(
    () => ({ ...paginationParams, id: taskId }),
    [paginationParams, taskId]
  ) as GetTasksItemPhotosParams

  const {
    data: competitorsPhotoControlTableData,
    isLoading: isTasksItemPhotosDataLoading,
    isError: isErrorTasksItemPhotos
  } = useQuery({
    retry: false,
    queryKey: [MONITORINGS_TASKS_KEY.tasksItemPhotos, tasksItemPhotosParams],
    queryFn: async () => await getTasksItemPhotos(tasksItemPhotosParams),
    select: tasksItemPhotosData => adaptCompetitorsPhotoControlDataSource(tasksItemPhotosData)
  })

  const {
    items,
    defaultItems,
    columns: modifiedColumns,
    onModify
  } = useTableSetting(TABLE_SETTING_KEYS.photoControlTab, columns)

  const {
    columns: columnsWithSearchTool,
    filteredDataSource: filteredMonitoringsTasksItemPhotosDataSource,
    isSearchToolOpened,
    toggleSearchTool
  } = useTableSearch({
    columns: modifiedColumns,
    dataSource: competitorsPhotoControlTableData?.dataSource,
    columnsSearchSchema,
    tableSearchQuerySchema
  })

  const toolbar: ReactNode = (
    <TableSettingsToolbar
      key='table-settings-toolbar'
      tableSettingButtonProps={ { items, defaultItems, onAccept: onModify } }
      tableSearchButtonProps={ { active: isSearchToolOpened, onClick: toggleSearchTool } }
    />
  )

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  useErrorRedirect(isErrorTasksItemPhotos)

  return (
    <ContentLayout>
      <ExtendedTable
        loading={ isTasksItemPhotosDataLoading }
        columns={ columnsWithSearchTool }
        dataSource={ filteredMonitoringsTasksItemPhotosDataSource }
        toolbar={ toolbar }
        pagination={ {
          ...pagination,
          total: competitorsPhotoControlTableData?.count,
          onChange: handlePaginationChange
        } }
      />
    </ContentLayout>
  )
}
